import { toast } from 'sonner';
import { ColorSwatchPicker } from '../ui/ColorSwatchPicker';
import { useEventTypes, useMyColorOverrides, useSetMyColorOverride } from '../../hooks/queries/useEventTypes';
import { useTaskTemplates } from '../../hooks/queries/useTaskTemplates';
import { EVENT_FAMILY_LABEL, type ColorTarget, type EventTypeFamily } from '../../services/calendar/eventTypeService';

interface Row {
  id: string;
  name: string;
  /** Couleur du cabinet. null : la tâche type n'en a pas. */
  base: string | null;
  target: ColorTarget;
}

/**
 * Section « Mes couleurs » de la page Mon compte : la personne remplace, pour elle
 * seule, la couleur d'un type d'événement ou d'une tâche type dans l'agenda.
 */
export function ProfileColorsSection() {
  const { data: eventTypes = [] } = useEventTypes();
  const { data: templates = [] } = useTaskTemplates(true);
  const { data: overrides = [] } = useMyColorOverrides();
  const setOverride = useSetMyColorOverride();

  const mine = new Map<string, string>();
  for (const o of overrides) {
    const id = o.event_type_id ?? o.task_template_id;
    if (id) mine.set(id, o.color);
  }

  const groups: { title: string; rows: Row[] }[] = [
    ...(['hearing', 'appointment'] as EventTypeFamily[]).map((family) => ({
      title: EVENT_FAMILY_LABEL[family],
      rows: eventTypes
        .filter((t) => t.family === family && t.is_active)
        .map((t) => ({ id: t.id, name: t.name, base: t.color, target: { eventTypeId: t.id } })),
    })),
    {
      title: 'Tâches types',
      rows: templates.map((t) => ({ id: t.id, name: t.content, base: t.color, target: { taskTemplateId: t.id } })),
    },
  ].filter((g) => g.rows.length > 0);

  if (groups.length === 0) return null;

  function save(target: ColorTarget, color: string | null) {
    setOverride.mutate(
      { target, color },
      { onError: (err) => toast.error(err instanceof Error ? err.message : 'Impossible d’enregistrer la couleur') },
    );
  }

  return (
    <div className="bg-cime-surface rounded-cime border border-neutral-200 p-6 space-y-4">
      <div>
        <h2 className="text-sm font-semibold text-neutral-500 uppercase tracking-wide">Mes couleurs</h2>
        <p className="mt-1 text-sm text-cime-text-muted">
          Les couleurs de l’agenda sont celles du cabinet. Vous pouvez en changer une pour vous :
          les autres personnes gardent la leur.
        </p>
      </div>
      {groups.map((group) => (
        <div key={group.title} className="space-y-1.5">
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">{group.title}</h3>
          <ul className="m-0 list-none divide-y divide-neutral-100 rounded-md border border-neutral-200 bg-white p-0">
            {group.rows.map((row) => {
              const own = mine.get(row.id) ?? null;
              return (
                <li key={row.id} className="flex items-center gap-3 px-3 py-2">
                  <ColorSwatchPicker
                    value={own ?? row.base}
                    label={`Ma couleur pour ${row.name}`}
                    onChange={(color) => save(row.target, color)}
                    onReset={own ? () => save(row.target, null) : undefined}
                    resetLabel="Revenir à la couleur du cabinet"
                  />
                  <span className="min-w-0 flex-1 truncate text-sm text-cime-text-primary">{row.name}</span>
                  {own ? (
                    <button
                      type="button"
                      onClick={() => save(row.target, null)}
                      className="text-xs text-cime-text-muted hover:text-cime-primary hover:underline"
                    >
                      Couleur du cabinet
                    </button>
                  ) : (
                    <span className="text-xs text-neutral-400">Couleur du cabinet</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}
